import * as React from "react";
import { Icon } from "../actions/Icon";
import { IconButton } from "../actions/IconButton";
import { LevelMeter } from "../media/LevelMeter";
import { formatHotkey, type HotkeyModifier } from "../../lib/hotkeys";

type HotkeyKeys = readonly (HotkeyModifier | (string & {}))[];

/**
 * Floating recorder HUD shown while recording: timer, pause, stop, restart, discard, mic level and camera. Always dark.
 */
export interface RecorderBarProps {
  time: string;
  paused?: boolean;
  level?: number;
  /** Defaults to Alt+Shift+P; pass the real combo when one is known (e.g. the desktop bridge's FR-RD5 accelerator). */
  pauseHotkey?: HotkeyKeys;
  /** Defaults to Alt+Shift+S; pass the real combo when one is known (e.g. the desktop bridge's FR-RD5 accelerator). */
  stopHotkey?: HotkeyKeys;
  onPause?: () => void;
  onStop?: () => void;
  onRestart?: () => void;
  onDiscard?: () => void;
  onMore?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

export function RecorderBar({
  time,
  paused,
  level = 0.7,
  pauseHotkey = ["Alt", "Shift", "P"],
  stopHotkey = ["Alt", "Shift", "S"],
  onPause,
  onStop,
  onRestart,
  onDiscard,
  onMore,
  className,
  style,
}: RecorderBarProps): JSX.Element {
  const pauseKey = formatHotkey(pauseHotkey);
  const stopKey = formatHotkey(stopHotkey);
  return (
    <div className={["gk-hud", className].filter(Boolean).join(" ")} style={style} role="toolbar" aria-label="Recording controls">
      <div className="gk-hud__time">
        <span className={"gk-recdot" + (paused ? " gk-recdot--paused" : "")} />
        {time}
      </div>
      <span className="gk-hud__sep" />
      <IconButton
        variant="solid"
        icon={paused ? "play" : "pause"}
        fill={paused}
        label={(paused ? "Resume" : "Pause") + " (" + pauseKey + ")"}
        iconSize={22}
        onClick={onPause}
      />
      <IconButton variant="record" stop label={"Stop and get link (" + stopKey + ")"} onClick={onStop} />
      {onRestart && <IconButton icon="arrow-counter-clockwise" label="Restart" iconSize={22} onClick={onRestart} />}
      <IconButton icon="trash" label="Discard" iconSize={22} onClick={onDiscard} />
      <span className="gk-hud__sep" />
      <div className="gk-hud__tail">
        <Icon name="microphone" />
        <LevelMeter size="sm" level={paused ? 0 : level} />
        <Icon name="video-camera" style={{ marginLeft: 6 }} />
        {onMore && <IconButton size="sm" icon="dots-three" label="More" onClick={onMore} />}
      </div>
    </div>
  );
}
